import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";
import { ArrowLeft, Copy, MessageCircle, Share2, TrendingUp, CheckCircle2, AlertTriangle, Package } from "lucide-react";

function Stat({ label, value, tid }) {
  return (
    <div data-testid={tid} className="min-w-0">
      <div className="text-[10px] font-semibold uppercase tracking-[0.15em] text-[#141414]/60">{label}</div>
      <div className="font-display leading-none mt-2 text-2xl sm:text-3xl md:text-4xl lg:text-5xl break-words">{value}</div>
    </div>
  );
}

export default function Impact() {
  const { t, settings, lang } = useApp();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("all");
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    api.get("/impact").then((r) => setData(r.data));
    api.get("/categories/resources").then((r) => setCategories(r.data));
  }, []);

  const catLabel = (key) => {
    const c = categories.find(x => x.key === key);
    if (c) return lang === "id" ? c.label_id : c.label_en;
    return t.resources.tabs[key] || key;
  };

  const tabs = ["all", "tracked", "low_stock", "fully_delivered"];
  const tabLabel = { all: "Semua", tracked: "Sedang Berjalan", low_stock: "Stok Rendah", fully_delivered: "Selesai" };

  const filtered = useMemo(() => {
    const items = data?.resources || [];
    if (tab === "tracked") return items.filter(r => r.quantity_total > 0 && r.progress_pct < 100);
    if (tab === "low_stock") return items.filter(r => r.low_stock);
    if (tab === "fully_delivered") return items.filter(r => r.quantity_total > 0 && r.progress_pct >= 100);
    return items;
  }, [data, tab]);

  const url = typeof window !== "undefined" ? window.location.href : "";
  const shareText = `${settings?.org_name || "Lepo Gete"} · Laporan Dampak — ${data?.totals?.delivered ?? 0} / ${data?.totals?.planned ?? 0} unit terkirim. Pantau: ${url}`;
  const copyLink = async () => { try { await navigator.clipboard.writeText(url); toast.success("Tautan disalin"); } catch { toast.error("Gagal menyalin"); } };
  const shareWA = () => window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, "_blank", "noopener,noreferrer");
  const nativeShare = async () => {
    if (navigator.share) { try { await navigator.share({ title: settings?.site_title || "Laporan Dampak", text: shareText, url }); return; } catch {} }
    copyLink();
  };

  if (!data) return <div className="max-w-4xl mx-auto p-10 text-[#141414]/50">Loading…</div>;

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-6 md:py-12">
      <Link to="/" data-testid="impact-back" className="inline-flex items-center gap-1 text-xs font-semibold text-[#141414]/60 hover:text-[#141414] mb-4">
        <ArrowLeft className="w-3.5 h-3.5" /> Beranda
      </Link>

      <div className="inline-flex items-center gap-2 flat-border rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] mb-4">
        <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--maroon)] animate-pulse" /> Laporan Dampak · Transparan
      </div>

      <h1 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl tracking-tight max-w-3xl">
        Setiap unit bantuan, kami catat.
      </h1>
      <p className="mt-3 text-sm sm:text-base text-[#141414]/70 max-w-2xl">
        Halaman ini menampilkan seluruh sumber daya yang direncanakan, telah dikirim, dan sisa yang masih perlu didistribusikan.
      </p>

      {/* Totals */}
      <section className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-6 md:divide-x md:divide-y-0 divide-y divide-[#141414]/15">
        <div className="md:pl-0"><Stat tid="impact-delivered" label="Terkirim" value={(data.totals.delivered || 0).toLocaleString("id-ID")} /></div>
        <div className="md:pl-8"><Stat tid="impact-planned" label="Direncanakan" value={(data.totals.planned || 0).toLocaleString("id-ID")} /></div>
        <div className="md:pl-8 pt-4 md:pt-0"><Stat tid="impact-avg" label="Rata-rata Progres" value={`${data.totals.avg_progress}%`} /></div>
        <div className="md:pl-8 pt-4 md:pt-0"><Stat tid="impact-fully" label="Selesai" value={`${data.totals.fully_delivered}/${data.totals.tracked_count}`} /></div>
      </section>

      {/* Share bar */}
      <div className="mt-6 flat-border rounded-2xl bg-[color:var(--cream)] p-3 flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[color:var(--maroon)] sm:mr-auto">Bagikan Laporan</div>
        <button onClick={shareWA} data-testid="impact-share-wa" className="flat-btn-primary rounded-full px-4 py-2 text-sm font-semibold flex items-center justify-center gap-2"><MessageCircle className="w-4 h-4" /> WhatsApp</button>
        <button onClick={copyLink} data-testid="impact-share-copy" className="flat-btn bg-white rounded-full px-4 py-2 text-sm font-semibold flex items-center justify-center gap-2"><Copy className="w-4 h-4" /> Salin Tautan</button>
        <button onClick={nativeShare} data-testid="impact-share-native" className="flat-btn bg-white rounded-full px-4 py-2 text-sm font-semibold flex items-center justify-center gap-2 sm:hidden"><Share2 className="w-4 h-4" /></button>
      </div>

      {/* Filter tabs */}
      <div className="mt-8 flex gap-2 flex-wrap">
        {tabs.map((k) => (
          <button
            key={k}
            data-testid={`impact-tab-${k}`}
            onClick={() => setTab(k)}
            className={`rounded-full px-3 py-1.5 text-xs font-semibold border transition-colors flex items-center gap-1.5 ${
              tab === k ? "bg-[color:var(--maroon)] text-[color:var(--cream)] border-[color:var(--maroon)]" : "bg-transparent border-[color:var(--ink)]"
            }`}
          >
            {k === "low_stock" && <AlertTriangle className="w-3.5 h-3.5" />}
            {k === "fully_delivered" && <CheckCircle2 className="w-3.5 h-3.5" />}
            {k === "tracked" && <TrendingUp className="w-3.5 h-3.5" />}
            {k === "all" && <Package className="w-3.5 h-3.5" />}
            {tabLabel[k]}
          </button>
        ))}
      </div>

      {/* Items */}
      <section className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
        {filtered.length === 0 && <div className="col-span-full text-sm text-[#141414]/50 py-10 text-center">Tidak ada data pada filter ini.</div>}
        {filtered.map((r) => (
          <article key={r.id} data-testid={`impact-item-${r.id}`} className={`flat-border rounded-2xl p-4 sm:p-5 bg-white ${r.low_stock ? "bg-amber-50" : ""}`}>
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="min-w-0">
                <div className="text-[10px] uppercase font-semibold tracking-wider text-[color:var(--maroon)]">{catLabel(r.category)}</div>
                <div className="font-display text-lg sm:text-xl mt-0.5 leading-tight break-words">{r.name}</div>
                {r.location && <div className="text-[11px] text-[#141414]/60 mt-0.5">{r.location}</div>}
              </div>
              {r.low_stock && <span className="shrink-0 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-amber-500 text-white flex items-center gap-1"><AlertTriangle className="w-2.5 h-2.5" strokeWidth={3} /> Stok Rendah</span>}
              {!r.low_stock && r.quantity_total > 0 && r.progress_pct >= 100 && <span className="shrink-0 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[color:var(--maroon)] text-[color:var(--cream)]">Selesai</span>}
            </div>
            {r.quantity_total > 0 ? (
              <>
                <div className="flex items-baseline justify-between mb-1.5">
                  <div className="text-xs font-mono">
                    <span className="font-bold text-base">{r.quantity_delivered.toLocaleString("id-ID")}</span>
                    <span className="text-[#141414]/50"> / {r.quantity_total.toLocaleString("id-ID")}</span>
                  </div>
                  <div className="text-xs font-mono text-[#141414]/70">Sisa {r.quantity_remaining.toLocaleString("id-ID")}</div>
                </div>
                <div className="h-2 progress-track rounded-full overflow-hidden">
                  <div className="h-full bg-[color:var(--maroon)] transition-all duration-500" style={{ width: `${r.progress_pct}%` }} />
                </div>
                <div className="mt-1.5 text-[11px] text-[#141414]/60">{r.progress_pct}% terkirim</div>
              </>
            ) : (
              <div className="text-sm text-[#141414]/70">{r.quantity || <span className="text-[#141414]/40">Belum ada target kuantitas</span>}</div>
            )}
          </article>
        ))}
      </section>
    </div>
  );
}
